export const SUBJECT_TYPES = {
  GAME: 0,
  VIDEO: 1,
}

export const VIDEO_SOURCE_TYPES = {
  HLS: 0,
  MP4: 1,
}

export const CUSTOM_TITLE_VALUE = '__custom__'

export const VIDEO_LINK_TITLE_OPTIONS = [
  { value: 'کیفیت ۳۶۰', label: 'کیفیت ۳۶۰' },
  { value: 'کیفیت ۴۸۰', label: 'کیفیت ۴۸۰' },
  { value: 'کیفیت ۷۲۰', label: 'کیفیت ۷۲۰' },
  { value: 'کیفیت ۱۰۸۰', label: 'کیفیت ۱۰۸۰' },
  { value: 'کیفیت 4K', label: 'کیفیت 4K' },
  { value: CUSTOM_TITLE_VALUE, label: 'عنوان دلخواه' },
]

export const VIDEO_PLAYER_TITLE_OPTIONS = [
  { value: 'کیفیت ۳۶۰', label: 'کیفیت ۳۶۰' },
  { value: 'کیفیت ۴۸۰', label: 'کیفیت ۴۸۰' },
  { value: 'کیفیت ۷۲۰', label: 'کیفیت ۷۲۰' },
  { value: 'کیفیت ۱۰۸۰', label: 'کیفیت ۱۰۸۰' },
  { value: 'HLS', label: 'HLS' },
  { value: CUSTOM_TITLE_VALUE, label: 'عنوان دلخواه' },
]

const VIDEO_URL_MAX_LENGTH = 2048

const getPresetTitleValues = (titleOptions = VIDEO_PLAYER_TITLE_OPTIONS) =>
  titleOptions
    .filter((option) => option.value !== CUSTOM_TITLE_VALUE)
    .map((option) => option.value)

export const parseVideoLinkTitle = (title = '', titleOptions = VIDEO_PLAYER_TITLE_OPTIONS) => {
  const value = (title || '').trim()
  const presetValues = getPresetTitleValues(titleOptions)

  if (presetValues.includes(value)) {
    return {
      title: value,
      titleOption: value,
      customTitle: '',
    }
  }

  return {
    title: value,
    titleOption: CUSTOM_TITLE_VALUE,
    customTitle: value,
  }
}

export const createEmptyVideo = () => {
  const defaultTitle = VIDEO_PLAYER_TITLE_OPTIONS[2].value

  return {
    id: null,
    title: defaultTitle,
    titleOption: defaultTitle,
    customTitle: '',
    originUrl: '',
    subtitleUrl: '',
  }
}

export const parseVideosForEdit = (videos = [], legacyVideo = null) => {
  const source = videos?.length ? videos : legacyVideo ? [legacyVideo] : []

  if (!source.length) {
    return [createEmptyVideo()]
  }

  return source.map((video) => {
    const titleData = parseVideoLinkTitle(video.title || '', VIDEO_PLAYER_TITLE_OPTIONS)

    return {
      id: video.id ?? null,
      originUrl: video.originUrl || video.playbackUrl || '',
      subtitleUrl: video.subtitleUrl || '',
      ...titleData,
    }
  })
}

export const validateSubtitleUrl = (url) => {
  const value = (url || '').trim()

  if (!value) {
    return null
  }

  if (value.length > VIDEO_URL_MAX_LENGTH) {
    return `حداکثر طول آدرس ${VIDEO_URL_MAX_LENGTH} کاراکتر است.`
  }

  if (!/^https:\/\//i.test(value)) {
    return 'آدرس زیرنویس باید کامل و با HTTPS شروع شود.'
  }

  return null
}

export const buildVideosPayload = (videos = []) => {
  if (!videos.length) {
    return { error: 'حداقل یک ویدیو برای پخش آنلاین الزامی است.' }
  }

  const payload = []

  for (let index = 0; index < videos.length; index += 1) {
    const video = videos[index]
    const originUrl = (video.originUrl || '').trim()
    const urlError = validateVideoUrl(originUrl)

    if (urlError) {
      return { error: `ویدیو ${index + 1}: ${urlError}` }
    }

    const title =
      video.titleOption === CUSTOM_TITLE_VALUE
        ? video.customTitle?.trim()
        : video.title?.trim()

    if (!title) {
      return { error: `ویدیو ${index + 1}: عنوان الزامی است.` }
    }

    const sourceType = detectVideoSourceType(originUrl)
    let subtitleUrl = null

    if (sourceType === VIDEO_SOURCE_TYPES.MP4) {
      const subtitle = (video.subtitleUrl || '').trim()

      if (subtitle) {
        const subtitleError = validateSubtitleUrl(subtitle)
        if (subtitleError) {
          return { error: `ویدیو ${index + 1}: ${subtitleError}` }
        }
        subtitleUrl = subtitle
      }
    }

    payload.push({
      id: video.id ?? null,
      title,
      originUrl,
      sourceType,
      subtitleUrl,
    })
  }

  return { payload }
}

const stripQueryAndHash = (url) => {
  try {
    const parsed = new URL(url)
    return parsed.pathname
  } catch {
    return url.split('?')[0].split('#')[0]
  }
}

export const detectVideoSourceType = (url) => {
  const path = stripQueryAndHash(url || '').toLowerCase()
  if (path.endsWith('.m3u8')) return VIDEO_SOURCE_TYPES.HLS
  if (path.endsWith('.mp4')) return VIDEO_SOURCE_TYPES.MP4
  return null
}

export const getHlsPlaybackUrl = (videos = [], legacyVideo = null) => {
  const source = videos?.length ? videos : legacyVideo ? [legacyVideo] : []

  const hlsVideo = source.find((video) => {
    const url = video?.originUrl || video?.playbackUrl || ''
    return (
      video?.sourceType === VIDEO_SOURCE_TYPES.HLS ||
      (video?.title || '').trim() === 'HLS' ||
      detectVideoSourceType(url) === VIDEO_SOURCE_TYPES.HLS
    )
  })

  const url = (hlsVideo?.originUrl || hlsVideo?.playbackUrl || '').trim()
  return url || null
}

export const getOnlinePlaybackLinks = (videos = [], legacyVideo = null) => {
  const source = videos?.length ? videos : legacyVideo ? [legacyVideo] : []

  return source
    .map((item) => ({
      id: item?.id,
      title: (item?.title || '').trim(),
      playbackUrl: typeof item?.playbackUrl === 'string' ? item.playbackUrl.trim() : '',
    }))
    .filter((item) => item.playbackUrl)
}

export const validateVideoUrl = (url) => {
  const value = (url || '').trim()

  if (!value) {
    return 'آدرس ویدیو الزامی است.'
  }

  if (value.length > VIDEO_URL_MAX_LENGTH) {
    return `حداکثر طول آدرس ${VIDEO_URL_MAX_LENGTH} کاراکتر است.`
  }

  if (!/^https:\/\//i.test(value)) {
    return 'آدرس باید کامل و با HTTPS شروع شود.'
  }

  const path = stripQueryAndHash(value).toLowerCase()
  if (!path.endsWith('.m3u8') && !path.endsWith('.mp4')) {
    return 'فرمت ویدئو باید m3u8 یا mp4 باشد.'
  }

  return null
}

export const parseBranchModelsForEdit = (subjectBranch = [], isVideo = false) => {
  if (!isVideo) return subjectBranch || []

  return (subjectBranch || []).map((branch) => ({
    ...branch,
    branchLinks: (branch.branchLinks || []).map((branchLink) => ({
      ...branchLink,
      downloadLinks: (branchLink.downloadLinks || []).map((downloadLink) => ({
        ...downloadLink,
        links: (downloadLink.links || []).map((linkItem) => ({
          ...linkItem,
          ...parseVideoLinkTitle(linkItem.title, VIDEO_LINK_TITLE_OPTIONS),
        })),
      })),
    })),
  }))
}

export const sanitizeBranchModelsForSubmit = (branchModels = [], isVideo = false) => {
  return branchModels.map((branch) => ({
    ...branch,
    branchLinks: (branch.branchLinks || []).map((branchLink) => ({
      ...branchLink,
      downloadLinks: (branchLink.downloadLinks || []).map((downloadLink) => ({
        ...downloadLink,
        links: (downloadLink.links || []).map((linkItem) => {
          const payload = {
            ...(linkItem.id != null ? { id: linkItem.id } : {}),
            link: linkItem.link,
            size: linkItem.size,
          }

          if (isVideo) {
            const title =
              linkItem.titleOption === CUSTOM_TITLE_VALUE
                ? linkItem.customTitle?.trim()
                : linkItem.title?.trim()
            payload.title = title || null
          }

          return payload
        }),
      })),
    })),
  }))
}
